/**
 * Sessions de charge OCPP persistées (localStorage).
 *
 * Même clé et même forme que la page Bornes (`zce_borne_sessions_v1`), afin que
 * l'état "en charge" soit partagé entre la page Bornes et la page Équipement
 * (WattelEM) et qu'il survive à la navigation / au rafraîchissement / au logout.
 */

export interface PersistedBorneSession {
  deviceSn: string;
  transactionId: number;
  chargeStartedAt: number;      // ms epoch — début de la charge
  startEnergyWh: number | null; // lecture meter au démarrage (si dispo)
  lastEnergyWh: number | null;  // dernière lecture meter vue
  sessionEnergyWh: number;      // énergie consommée pendant cette session (Wh)
  lastTickAt: number;           // dernier tick d'intégration
  fallbackPowerW: number;       // puissance de simulation si pas de meter réel
}

const SESSION_KEY = 'zce_borne_sessions_v1';

/** Événement émis quand une session de charge change (start/stop/update). */
export const BORNE_SESSION_EVENT = 'zce-borne-session-changed';

function emitChange(deviceSn: string): void {
  try {
    window.dispatchEvent(new CustomEvent(BORNE_SESSION_EVENT, { detail: { deviceSn } }));
  } catch {
    /* SSR / pas de window */
  }
}

export function loadBorneSessions(): Record<string, PersistedBorneSession> {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveBorneSessions(sessions: Record<string, PersistedBorneSession>): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessions));
  } catch {
    /* quota */
  }
}

export function getBorneSession(deviceSn: string): PersistedBorneSession | null {
  return loadBorneSessions()[deviceSn] || null;
}

export function startBorneSession(session: PersistedBorneSession): void {
  const all = loadBorneSessions();
  all[session.deviceSn] = session;
  saveBorneSessions(all);
  emitChange(session.deviceSn);
}

export function endBorneSession(deviceSn: string): void {
  const all = loadBorneSessions();
  delete all[deviceSn];
  saveBorneSessions(all);
  emitChange(deviceSn);
}

export function updateBorneSession(deviceSn: string, patch: Partial<PersistedBorneSession>): void {
  const all = loadBorneSessions();
  if (!all[deviceSn]) return;
  all[deviceSn] = { ...all[deviceSn], ...patch };
  saveBorneSessions(all);
  emitChange(deviceSn);
}

/** Puissance nominale (W) indicative, déduite du type ou du SN (info uniquement). */
export function nominalPowerW(typeOrSn?: string | null): number {
  const s = (typeOrSn || '').toLowerCase();
  if (s.includes('22')) return 22000;   // Wattel 22 (22 kW)
  if (s.includes('wall')) return 7400;  // WattelWall
  if (s.includes('go')) return 7400;    // WattelGo
  if (s.includes('7')) return 7400;     // Wattel 7 (7.4 kW)
  return 7400;
}

/**
 * Valeurs live d'une session de charge — UNIQUEMENT les vraies données OCPP.
 *
 * AUCUNE simulation : la puissance et l'énergie proviennent exclusivement du
 * backend (charging_transactions + cache OCPP). Si la borne n'émet pas encore
 * de MeterValues, on renvoie 0 (et non une valeur inventée).
 *
 * Repli après refresh : tant que le 1er poll OCPP n'a pas répondu, on réaffiche
 * la DERNIÈRE énergie réelle mémorisée dans la session persistée (`sessionEnergyWh`),
 * ce qui reste une vraie valeur (pas une simulation).
 *
 * `isCharging` est piloté par l'état OCPP réel (`isActive`) OU par la session
 * persistée (localStorage) — pour que l'état "en charge" survive au refresh
 * jusqu'à ce que l'utilisateur appuie sur OFF.
 */
export function computeLiveBorne(
  session: PersistedBorneSession | null,
  ocpp?: {
    isActive?: boolean;
    currentPowerW?: number | null;
    currentEnergyWh?: number | null;
    currentEnergyKwh?: number | null;
  } | null,
): { isCharging: boolean; powerW: number; energyWh: number; hasRealData: boolean } {
  const isCharging = !!ocpp?.isActive || !!session;
  if (!isCharging) return { isCharging: false, powerW: 0, energyWh: 0, hasRealData: false };
  const realPowerW = ocpp?.currentPowerW != null ? Number(ocpp.currentPowerW) : 0;
  const realEnergyWh = ocpp?.currentEnergyWh != null
    ? Number(ocpp.currentEnergyWh)
    : (ocpp?.currentEnergyKwh != null ? Number(ocpp.currentEnergyKwh) * 1000 : 0);
  // Énergie affichée : vraie valeur OCPP du poll si dispo, sinon dernière vraie
  // valeur mémorisée (survie au refresh). Jamais de valeur calculée/simulée.
  const energyWh = realEnergyWh > 0 ? realEnergyWh : (session?.sessionEnergyWh || 0);
  const hasRealData = realPowerW > 0 || energyWh > 0;
  return { isCharging, powerW: realPowerW, energyWh, hasRealData };
}
